import { DisplayType, DocumentIssue } from 'common/types';

export type DefaultLineItemsAPI = {
    [key: string]: {
        [key: string]: {
            EMERGENCY: string[];
            ROUTINE: string[];
            URGENT: string[];
        };
    };
};

export type OpsAccountAPI = {
    id: string | number;
    display: string;
    firstName: string;
    lastName: string;
    fullName: string;
    department: string[];
};

export enum WORK_ORDER_ISSUE {
    ASSET_RECOMMENDATION = 'ASSET_RECOMMENDATION',
    CLIENT = 'CLIENT',
    CONTRACTOR = 'CONTRACTOR',
    DISTRIBUTOR = 'DISTRIBUTOR',
    FINANCE = 'FINANCE',
    HOMEOWNER = 'HOMEOWNER',
    NO_COVERAGE = 'NO_COVERAGE',
    NOT_ACCEPTED = 'NOT_ACCEPTED',
    POTENTIAL_DAMAGE = 'POTENTIAL_DAMAGE',
    QUALITY_APPROVAL_REQUIRED = 'QUALITY_APPROVAL_REQUIRED',
    QUALITY_REJECTED = 'QUALITY_REJECTED',
    REQUIREMENT_REQUIRED = 'REQUIREMENT_REQUIRED',
    REQUIREMENT = 'REQUIREMENT',
    SCHEDULE_HOLD = 'SCHEDULE_HOLD',
    UNASSIGNED = 'UNASSIGNED',
    QUALITY_APPROVED = 'QUALITY_APPROVED',
}

export enum QUOTE_ISSUE {
    CLIENT = 'CLIENT',
    CONTRACTOR = 'CONTRACTOR',
    DESIGN_REVIEW_REQUIRED = 'DESIGN_REVIEW_REQUIRED',
    DESIGN_REVIEW_REJECTED = 'DESIGN_REVIEW_REJECTED',
    DISTRIBUTOR = 'DISTRIBUTOR',
    HOMEOWNER = 'HOMEOWNER',
    QUALITY_APPROVED = 'QUALITY_APPROVED',
    QUALITY_REJECTED = 'QUALITY_REJECTED',
    QUALITY_REVIEW_REQUIRED = 'QUALITY_REVIEW_REQUIRED',
    QUALITY_APPROVAL_REQUIRED = 'QUALITY_APPROVAL_REQUIRED',
}

export type WorkOrderIssueAPI = DocumentIssue;

export type QuoteIssueAPI = DocumentIssue;

export type OrderIssueAPI = DisplayType<ORDER_ISSUE> & {
    meta: string;
    reasons: {
        [key: string]: DisplayType;
    };
};

export enum ORDER_ISSUE {
    CLIENT = 'CLIENT',
    DISTRIBUTOR = 'DISTRIBUTOR',
    CONTRACTOR = 'CONTRACTOR',
    CLIENT_INTEGRATION = 'CLIENT_INTEGRATION',
}

enum CLIENT_ORDER_ISSUE {
    CLIENT = 'CLIENT',
    DISTRIBUTOR = 'DISTRIBUTOR',
}

export type ClientOrderIssueAPI = DisplayType<CLIENT_ORDER_ISSUE> & {
    meta: string;
    reasons: {
        [key: string]: DisplayType;
    };
};

export type WorkOrderIssueRejectedTypeAPI = DisplayType;

enum FINANCIAL_SYNC_QUEUE_STATUS {
    QUEUE = 'QUEUE',
    RECEIVED = 'RECEIVED',
    SYNCING = 'SYNCING',
    SYNCED = 'SYNCED',
    FAILED = 'FAILED',
    ERROR = 'ERROR',
    MANUALLY_RESOLVED = 'MANUALLY_RESOLVED',
}

export type FinancialSyncQueueStatusAPI = DisplayType<
    keyof typeof FINANCIAL_SYNC_QUEUE_STATUS
>;

export enum FINANCIAL_SYNC_STATUS {
    REQUIRED_SYNC = 'REQUIRED_SYNC',
    SYNCING = 'SYNCING',
    SYNCED = 'SYNCED',
    FAILED = 'FAILED',
    ONLY_FOR_TESTING = 'ONLY_FOR_TESTING',
}

export type FinancialSyncStatusAPI = { id: keyof typeof FINANCIAL_SYNC_STATUS };

export type FinancialSyncQueueModelAPI = DisplayType;

export type FinancialSyncQueueResolveReasonsAPI = DisplayType;

export type ClientBusinessChannelAPI = DisplayType;

export type QuoteReopenReasonsAPI = DisplayType & {
    active: boolean;
};

enum QUOTE_REQUEST_REVISION_TYPE {
    EQUIPMENT_VERIFICATION_REQUIRED = 'EQUIPMENT_VERIFICATION_REQUIRED',
    PHOTOS_REQUIRED = 'PHOTOS_REQUIRED',
    WORK_JUSTIFICATION = 'WORK_JUSTIFICATION',
    PRICE_JUSTIFICATION = 'PRICE_JUSTIFICATION',
    PRICING_REQUIRED = 'PRICING_REQUIRED',
    SIZE_LOAD_CONFIRMATION = 'SIZE_LOAD_CONFIRMATION',
    MISSING_EQUIPMENT = 'MISSING_EQUIPMENT',
    WRONG_EQUIPMENT = 'WRONG_EQUIPMENT',
    LOAD_CALC = 'LOAD_CALC',
    BLOCK_LOAD = 'BLOCK_LOAD',
    OTHER = 'OTHER',
}
export type QuoteRequestRevisionTypeAPI = DisplayType<
    keyof typeof QUOTE_REQUEST_REVISION_TYPE
> & { parentId: string };

export enum QUOTE_REJECT_TYPE {
    THIRD_PARTY_REJECT_REQUEST = 'THIRD_PARTY_REJECT_REQUEST',
    NEED_TO_REVISE = 'NEED_TO_REVISE',
    ALTERNATE_QUOTE_NEEDED = 'ALTERNATE_QUOTE_NEEDED',
    ADDITIONAL_EQUIPMENT_NEEDED = 'ADDITIONAL_EQUIPMENT_NEEDED',
    TOO_EXPENSIVE = 'TOO_EXPENSIVE',
    REPAIR_NEEDED = 'REPAIR_NEEDED',
    REPLACE_NEEDED = 'REPLACE_NEEDED',
    OTHER = 'OTHER',
}
export type QuoteRejectTypeAPI = DisplayType<keyof typeof QUOTE_REJECT_TYPE> & {
    parentId: string;
};

export enum CONTRACTOR_FILE_TYPE {
    W9 = 'W9',
    EPA = 'EPA',
    PROOF_OF_INSURANCE = 'PROOF_OF_INSURANCE',
    QUALIFICATIONS = 'QUALIFICATIONS',
    FLAT_RATE_PAYOUT = 'FLAT_RATE_PAYOUT',
    CODE_OF_CONDUCT = 'CODE_OF_CONDUCT',
    COMPANY_MSA = 'COMPANY_MSA',
    DATA_CONSENT_AGREEMENT = 'DATA_CONSENT_AGREEMENT',
    TAX_SALES_CERTIFICATE = 'TAX_SALES_CERTIFICATE',
    SECRETARY_OF_STATE_REGISTRATION = 'SECRETARY_OF_STATE_REGISTRATION',
}

export type ContractorFileTypeAPI = DisplayType<
    keyof typeof CONTRACTOR_FILE_TYPE
> & {
    adminUseOnly: boolean;
};

export enum CONTRACTOR_TEAM_MEMBER_NOTIFICATION {
    DISPATCHING = 'DISPATCHING',
    WORK_REMINDERS = 'WORK_REMINDERS',
    BACK_OFFICE = 'BACK_OFFICE',
    GENERAL = 'GENERAL',
    NEW_USER = 'NEW_USER',
}

export type ContractorTeamMemberTitleAPI = DisplayType<
    keyof typeof CONTRACTOR_TEAM_MEMBER_NOTIFICATION
> & {
    overide: boolean;
};

enum CONTRACTOR_TEAM_MEMBER_TITLE {
    TECHNICIAN_ASSISTANT = 'TECHNICIAN_ASSISTANT',
    INSTALL_TECHNICIAN = 'INSTALL_TECHNICIAN',
    MASTER_TECHNICIAN = 'MASTER_TECHNICIAN',
    SERVICE_TECHNICIAN = 'SERVICE_TECHNICIAN',
    OFFICE_PERSONNEL = 'OFFICE_PERSONNEL',
    DISPATCHER = 'DISPATCHER',
    OWNER = 'OWNER',
    OFFICE_MANAGER = 'OFFICE_MANAGER',
    FINANCE_MANAGER = 'FINANCE_MANAGER',
    ELECTRICIAN = 'ELECTRICIAN',
    PLUMBER = 'PLUMBER',
    CARPENTER = 'CARPENTER',
    MECHANICAL_ENGINEER = 'MECHANICAL_ENGINEER',
}

export type ContractorTeamMemberNotificationAPI = DisplayType<
    keyof typeof CONTRACTOR_TEAM_MEMBER_TITLE
> & {
    overide: boolean;
};

export enum CONTRACTOR_TEAM_MEMBER_FILE_TYPE {
    BADGE_ID = 'BADGE_ID',
    EPA = 'EPA',
    CERTIFICATION = 'CERTIFICATION',
    TEAM_MEMBER_MSA = 'TEAM_MEMBER_MSA',
}

export type ContractorTeamMemberFileTypeAPI = DisplayType<
    keyof typeof CONTRACTOR_TEAM_MEMBER_FILE_TYPE
>;

export enum CONTRACTOR_JOB_TYPE_ELIGIBILITY_TYPE {
    DIRECT_SUPPLY = 'DIRECT_SUPPLY',
    DISPATCH = 'DISPATCH',
    ASURE_CERTIFIED = 'ASURE_CERTIFIED',
}

export type contractorJobTypeEligibilityTypeAPI = DisplayType<
    keyof typeof CONTRACTOR_JOB_TYPE_ELIGIBILITY_TYPE
>;

enum QUOTE_REASON {
    CREATE = 'CREATE',
    INSPECTION = 'INSPECTION',
    NO_ORIGIN_REPAIR = 'NO_ORIGIN_REPAIR',
    NO_ORIGIN_REPLACE = 'NO_ORIGIN_REPLACE',
    REPAIR = 'REPAIR',
    REPLACE = 'REPLACE',
    TEMPLATE = 'TEMPLATE',
    UNDER_CLIP = 'UNDER_CLIP',
}

export type QuoteReasonsAPI = {
    [key in QUOTE_REASON]: {
        [key: string]: DisplayType;
    };
};

export enum ORDER_FULFILLMENT_TYPE {
    CONTRACTOR_SUPPLIED = 'CONTRACTOR_SUPPLIED',
    DELIVERY = 'DELIVERY',
    PICK_UP = 'PICK_UP',
}

export type OrderFulfillmentTypeAPI = DisplayType<
    keyof typeof ORDER_FULFILLMENT_TYPE
> & {
    meta: string;
};

export enum PROPERTY_STATUS {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

export type PropertyStatusAPI = DisplayType<keyof typeof PROPERTY_STATUS> & {
    meta: string;
};

export enum PROJECT_STATUS {
    CREATED = 'CREATED',
    IN_PROCESS = 'IN_PROCESS',
    CLOSED = 'CLOSED',
    CANCELLED = 'CANCELLED',
    ON_HOLD = 'ON_HOLD',
}

export type ProjectStatusAPI = DisplayType<keyof typeof PROJECT_STATUS> & {
    active: boolean;
};

enum PROJECT_SCOPE_TYPE {
    INITIAL_SCOPE = 'INITIAL_SCOPE',
    CHANGE_ORDER = 'CHANGE_ORDER',
    DEDUCTIONS = 'DEDUCTIONS',
}

export type ProjectScopeTypeAPI = DisplayType<
    keyof typeof PROJECT_SCOPE_TYPE
> & {
    active: boolean;
};

export type ProjectAPI = {
    clientId: number;
    created: string;
    createdById: 16984;
    description: string;
    endDate: string | null;
    estEndDate: string;
    estStartDate: string;
    exclusiveDispatching: boolean;
    folderId: number | null;
    id: number;
    name: string;
    number: string;
    ownerId: number;
    propertyId: number;
    refContractId: number | null;
    startDate: string | null;
    statusId: string;
    typeId: string;
    updated: string;
};

enum PROJECT_TYPE {
    PLANNED_REPLACEMENT = 'PLANNED_REPLACEMENT',
    ASSET_TAGGING = 'ASSET_TAGGING',
    PREVENTATIVE_MAINTENANCE = 'PREVENTATIVE_MAINTENANCE',
}

export type ProjectTypeAPI = DisplayType<keyof typeof PROJECT_TYPE> & {
    active: boolean;
};

export type WorkOrderRequirementApprovalReasonAPI = DisplayType & {
    emphasize?: string;
    additionalReasons?: DisplayType[];
    categories: string[];
};

export type WorkOrderPhotoTypeAPI = DisplayType & {
    description: string;
    imageNames: string[];
    deprecatedDescriptions: string[];
    models: {
        [key: string]: {
            model: string;
            clientDefinedRequirements: boolean;
            required: boolean;
            minimumRequired: number;
        };
    };
    deprecated: boolean;
    postProcessing: {
        identification: boolean;
        classifications: boolean;
        integrity: boolean;
    };
};

export type WorkOrderVideoTypeAPI = DisplayType & {
    description: string;
    imageNames: string[];
    deprecatedDescriptions: string[];
    models: {
        [key: string]: {
            model: string;
            clientDefinedRequirements: boolean;
            required: boolean;
            minimumRequired: number;
        };
    };
    deprecated: boolean;
};

enum WORK_ORDER_PDF_TYPE {
    CLIENT_QUOTE = 'CLIENT_QUOTE',
    SUMMARY_OF_WORK = 'SUMMARY_OF_WORK',
}

export type WorkOrderPDFTypeAPI = DisplayType<
    keyof typeof WORK_ORDER_PDF_TYPE
> & {
    attachmentTypeId: keyof typeof FOLDER_ATTACHMENT_TYPE;
    title: string;
};

enum QUOTE_PDF_TYPE {
    CLIENT_QUOTE = 'CLIENT_QUOTE',
    SUMMARY_OF_WORK = 'SUMMARY_OF_WORK',
}

export type QuotePDFTypeAPI = DisplayType<keyof typeof QUOTE_PDF_TYPE> & {
    attachmentTypeId: keyof typeof FOLDER_ATTACHMENT_TYPE;
    jsReportTemplate: string;
    meta: string;
};

export enum ASSET_PHOTO_TYPE {
    ENVIRONMENT = 'ENVIRONMENT',
    EQUIPMENT = 'EQUIPMENT',
    NAME_PLATE = 'NAME_PLATE',
    QR_CODE = 'QR_CODE',
    OTHER = 'OTHER',
    GAUGE = 'GAUGE',
    STARTUP_CHECKLIST = 'STARTUP_CHECKLIST',
    TRIAGE_CHECKLIST = 'TRIAGE_CHECKLIST',
}

export type AssetPhotoTypeAPI = DisplayType<keyof typeof ASSET_PHOTO_TYPE> & {
    description: string;
    imageNames: string[];
    deprecatedDescriptions: string[];
    models: {
        [key: string]: {
            model: string;
            clientDefinedRequirements: boolean;
            required: boolean;
            minimumRequired: number;
        };
    };
    deprecated: boolean;
    postProcessing: {
        identification: boolean;
        classifications: boolean;
        integrity: boolean;
    };
};

export type AssetRepairReasonAPI = DisplayType;

enum ASSET_RECOMMENDATION_TYPE {
    NO_ACTION = 'NO_ACTION',
    REPAIR = 'REPAIR',
    REPLACE = 'REPLACE',
}

export type AssetRecommendationTypeAPI = DisplayType<
    keyof typeof ASSET_RECOMMENDATION_TYPE
>;

enum ASSET_UNTAGGABLE_REASON {
    ASSET_INACCESSIBLE = 'ASSET_INACCESSIBLE',
    NAMEPLATE_ILLEGIBLE = 'NAMEPLATE_ILLEGIBLE',
    RESIDENCE_INACCESSIBLE = 'RESIDENCE_INACCESSIBLE',
    INSUFFICIENT_TIME = 'INSUFFICIENT_TIME',
    OTHER = 'OTHER',
}

export type AssetUntaggableReasonAPI = DisplayType<
    keyof typeof ASSET_UNTAGGABLE_REASON
>;

export enum ASSET_CONFIGURATION_CATEGORY {
    INDOOR = 'INDOOR',
    OUTDOOR = 'OUTDOOR',
    ACCESSORY = 'ACCESSORY',
    CONTROLLER = 'CONTROLLER',
}

export type AssetConfigurationCategoryAPI =
    DisplayType<ASSET_CONFIGURATION_CATEGORY> & {
        noAttributes?: boolean;
    };

export interface AssetManufacturerAPI {
    id: string;
    display: string;
    active?: boolean;
}

export enum MANAGED_PRODUCT_CATEGORY {
    LABOR = 'LABOR',
    EQUIPMENT = 'EQUIPMENT',
    PART = 'PART',
    CUSTOM = 'CUSTOM',
    SHIPPING = 'SHIPPING',
    SUPPLIED = 'SUPPLIED',
    FEE = 'FEE',
    PACKAGES = 'PACKAGES',
    MIGRATED = 'MIGRATED',
    BUNDLE = 'BUNDLE',
}

export type ManagedProductCategoryAPI =
    DisplayType<MANAGED_PRODUCT_CATEGORY> & {
        active: boolean;
        img: string;
        meta: string;
        skuAbbreviation: string;
        system: boolean;
        whitelist: boolean;
        whitelistClient: boolean;
        whitelistContractor: boolean;
        subCategories: (DisplayType & {
            active: boolean;
            img: string;
            quoteOverride: string;
            skuAbbreviation: string;
            system: boolean;
            whitelist: boolean;
            whitelistClient: boolean;
            whitelistContractor: boolean;
            tradeTypes: {
                id: string;
                subTypes: (DisplayType & {
                    active: boolean;
                    skuAbbreviation: string;
                })[];
            }[];
        })[];
    };

enum MANAGED_PRODUCT_ATTRIBUTE_TYPE {
    CAPACITY = 'CAPACITY',
    DIMENSIONS = 'DIMENSIONS',
    PERFORMANCE = 'PERFORMANCE',
    PROPERTY = 'PROPERTY',
    RATING = 'RATING',
    TYPE = 'TYPE',
}

export type ManagedProductAttributeTypeAPI =
    DisplayType<MANAGED_PRODUCT_ATTRIBUTE_TYPE> & {
        meta: string;
        subTypes: {
            [key: string]: DisplayType & {
                description: string;
                featureDisplayPrefix: string;
                isFeature: boolean;
            };
        };
    };

enum MANAGED_PRODUCT_PRICING_TYPE {
    CLIENT = 'CLIENT',
    DEFAULT = 'DEFAULT',
}

export type ManagedProductPricingTypeAPI =
    DisplayType<MANAGED_PRODUCT_PRICING_TYPE>;

enum MANAGED_VENDOR_STATUS {
    CREATED = 'CREATED',
    VALIDATED = 'VALIDATED',
    SO_CREATED = 'SO_CREATED',
    INVOICED = 'INVOICED',
    AR_PARTIAL = 'AR_PARTIAL',
    AR_PAID = 'AR_PAID',
    FAILED = 'FAILED',
    NO_LINE_ITEM = 'NO_LINE_ITEM',
}

export type ManagedVendorStatusAPI = DisplayType<MANAGED_VENDOR_STATUS> & {
    meta: string;
};

export type ManagedVendorTypeAPI = DisplayType;

enum MANAGED_VENDOR_DISTRIBUTION_TYPE {
    PRODUCT_AVAILABILITY = 'PRODUCT_AVAILABILITY',
    TRADE_TYPE = 'TRADE_TYPE',
    FULFILLMENT = 'FULFILLMENT',
}

export type ManagedVendorDistributionTypeAPI = {
    id: keyof typeof MANAGED_VENDOR_DISTRIBUTION_TYPE;
    keys: {
        display: string;
        id: string;
        values: DisplayType<keyof typeof DOCUMENT_TRADE_TYPE>[];
    }[];
};

enum MANAGED_VENDOR_INTEGRATION_TYPE {
    ORDERING = 'ORDERING',
    PAYMENTS = 'PAYMENTS',
    INVENTORY = 'INVENTORY',
}

export type ManagedVendorIntegrationTypeAPI = {
    id: keyof typeof MANAGED_VENDOR_INTEGRATION_TYPE;
    clients: (DisplayType & {
        distributors: DisplayType[];
    })[];
};

export enum ASSET_STATUS {
    CAPTURED = 'CAPTURED',
    RECAPTURED = 'RECAPTURED',
    PROCESSING = 'PROCESSING',
    TAGGED = 'TAGGED',
    CONFIRM = 'CONFIRM',
    RETIRED = 'RETIRED',
    RECAPTURE = 'RECAPTURE',
    PARTIALLY_TAGGED = 'PARTIALLY_TAGGED',
}

export type AssetStatusAPI = DisplayType<ASSET_STATUS> & {
    meta: string;
    system: boolean;
};

enum ASSET_RETIREMENT_REASON {
    ERROR = 'ERROR',
    REPLACEMENT = 'REPLACEMENT',
    OTHER = 'OTHER',
}

export type AssetRetirementReasonAPI = DisplayType<ASSET_RETIREMENT_REASON> & {
    meta: string;
};

export enum PRODUCT_CATEGORY {
    LABOR = 'LABOR',
    EQUIPMENT = 'EQUIPMENT',
    PART = 'PART',
    CUSTOM = 'CUSTOM',
    SHIPPING = 'SHIPPING',
    SUPPLIED = 'SUPPLIED',
    FEE = 'FEE',
    PACKAGES = 'PACKAGES',
    MIGRATED = 'MIGRATED',
    WARRANTY = 'WARRANTY',
    BUNDLE = 'BUNDLE',
}

export type ProductCategoryAPI = DisplayType<PRODUCT_CATEGORY> & {
    active: boolean;
    img: string;
    meta: string;
    skuAbbreviation: string;
    system: boolean;
    whitelist: boolean;
    whitelistClient: boolean;
    whitelistContractor: boolean;
    subCategories: ProductSubCategory[];
};

export type ProductSubCategory = DisplayType & {
    active: boolean;
    img: string;
    quoteOverride: string;
    skuAbbreviation: string;
    system: boolean;
    whitelist: boolean;
    whitelistClient: boolean;
    whitelistContractor: boolean;
    tradeTypes: {
        id: string;
        subTypes: (DisplayType & {
            active: boolean;
            skuAbbreviation: string;
        })[];
    }[];
};

export enum SERVICE_TYPE {
    SINGLE_FAMILY = 'SINGLE_FAMILY',
    MULTI_DWELLING = 'MULTI_DWELLING',
    COMMERCIAL = 'COMMERCIAL',
    RESIDENTIAL = 'RESIDENTIAL',
    OCCUPIED = 'OCCUPIED',
    UNOCCUPIED = 'UNOCCUPIED',
    INDUSTRIAL = 'INDUSTRIAL',
    LIGHT_COMMERCIAL = 'LIGHT_COMMERCIAL',
    NEW_CONSTRUCTION = 'NEW_CONSTRUCTION,',
    PROJECTS = 'PROJECTS',
    RETAIL = 'RETAIL',
    RECALL = 'RECALL',
    HOME_WARRANTY = 'HOME_WARRANTY',
    SERVICE = 'SERVICE',
}

export type ContractorServiceTypeAPI = {
    [key in SERVICE_TYPE]: DisplayType<keyof typeof SERVICE_TYPE> & {
        key: string;
    };
};

enum CONTRACTOR_QUALIFICATION_TYPE {
    BACKGROUND_CHECK = 'BACKGROUND_CHECK',
    CERTIFICATION = 'CERTIFICATION',
    LICENSE = 'LICENSE',
}

export type ContractorQualificationTypeAPI = DisplayType<
    keyof typeof CONTRACTOR_QUALIFICATION_TYPE
>;

export enum QUOTE_TYPE {
    REPAIR = 'REPAIR',
    REPLACE = 'REPLACE',
    NO_ORIGIN_REPAIR = 'NO_ORIGIN_REPAIR',
    NO_ORIGIN_REPLACE = 'NO_ORIGIN_REPLACE',
    EQUIPMENT_ONLY = 'EQUIPMENT_ONLY',
}

export type QuoteTypeAPI = {
    id: keyof typeof QUOTE_TYPE;
    legacyId: string;
    display: string;
    active: boolean;
    meta: string;
    requiresContactInfo: boolean;
    hasOriginatingDocument: boolean;
    createsFollowupDocument: boolean;
};

export enum ACCOUNT_DEPARTMENT_ASSIGNMENT_TYPE {
    JOB_MGMT = 'JOB_MGMT',
    BID_DESK = 'BID_DESK',
    TTD = 'TTD',
    CND = 'CND',
    SALES = 'SALES',
    PROJECTS = 'PROJECTS',
    BUSINESS_OPERATIONS = 'BUSINESS_OPERATIONS',
    SALES_CALL_CENTER = 'SALES_CALL_CENTER',
    MARKETING = 'MARKETING',
    FINANCE = 'FINANCE',
    ENGINEERING = 'ENGINEERING',
    ORDER_MANAGEMENT = 'ORDER_MANAGEMENT',
}

export type AccountDepartmentAssignmentTypeAPI = DisplayType<
    keyof typeof ACCOUNT_DEPARTMENT_ASSIGNMENT_TYPE
> & {
    description: string;
};

export enum ACCOUNT_DEPARTMENT_TYPE {
    ACCOUNT_MANAGER = 'ACCOUNT_MANAGER',
    AP_FINANCE = 'AP_FINANCE',
    BID_DESK = 'BID_DESK',
    CONTRACTOR_RELATIONS = 'CONTRACTOR_RELATIONS',
    ENGINEERING = 'ENGINEERING',
    FINANCE = 'FINANCE',
    OPERATIONS = 'OPERATIONS',
    ORDER_MANAGEMENT = 'ORDER_MANAGEMENT',
    SALES = 'SALES',
    PROJECT_MANAGEMENT = 'PROJECT_MANAGEMENT',
}

export type AccountDepartmentTypeAPI = DisplayType<
    keyof typeof ACCOUNT_DEPARTMENT_TYPE
>;

export enum DOCUMENT_TRADE_TYPE {
    HVAC = 'HVAC',
    PLUMBING = 'PLUMBING',
    ELECTRICAL = 'ELECTRICAL',
}

export enum DOCUMENT_TRADE_SUB_TYPE {
    RESIDENTIAL = 'RESIDENTIAL',
    COMMERCIAL = 'COMMERCIAL',
    WATERHEATER = 'WATERHEATER',
}

export type DocumentTradeTypeAPI<T = keyof typeof DOCUMENT_TRADE_TYPE> =
    DisplayType<T> & {
        active?: boolean;
        skuAbbreviation?: string;
        tradeSubTypes?: {
            active: boolean;
            display: string;
            id: string;
            skuAbbreviation: string;
        }[];
        userRelevance?: number;
    };

export enum DOCUMENT_TYPE {
    QUOTE = 'QUOTE',
    ORDER = 'ORDER',
    WORK_ORDER = 'WORK_ORDER',
    DRAFT = 'DRAFT',
    INVOICE = 'INVOICE',
    CREDIT_MEMO = 'CREDIT_MEMO',
}

export type DocumentTypeAPI = DisplayType<DOCUMENT_TYPE> & {
    eventModelName: string;
};

export type FinancialInvoiceLineOfBusinessesAPI = {
    billing: string[];
    payment: string[];
};

export type FinancialSalesOrderLineOfBusinessesAPI = {
    billing: string[];
    billingMerge: string[];
};

type SchemaType = 'string' | 'object' | 'number';

export type ClientSettingAPI = DisplayType & {
    settings: {
        schema: {
            properties: {
                settingTypeId: {
                    type: SchemaType;
                };
                setting: {
                    type: SchemaType;
                    properties: {
                        quoting: {
                            type: SchemaType;
                            properties: {
                                routine: {
                                    type: SchemaType;
                                };
                                urgent: {
                                    type: SchemaType;
                                };
                                emergency: {
                                    type: SchemaType;
                                };
                            };
                        };
                        completion: {
                            type: SchemaType;
                            properties: {
                                routine: {
                                    type: SchemaType;
                                };
                                urgent: {
                                    type: SchemaType;
                                };
                                emergency: {
                                    type: SchemaType;
                                };
                            };
                        };
                    };
                };
            };
            required: string[];
            title: string;
            type: string;
        };
        types?: {
            [key: string]: DisplayType & {
                segments: { [key: string]: DisplayType };
            };
        };
        scope: string;
    };
};

export type ClientSettingByIdAPI = object;
export type ClientSettingByIdParams = {
    clientId: number;
    settingId: CLIENT_SETTINGS;
};

export enum LINE_OF_BUSINESS {
    EQUIPMENT = 'EQUIPMENT',
    ECOMMERCE = 'ECOMMERCE',
    INTEGRATED_SERVICES = 'INTEGRATED_SERVICES',
    CONSUMER = 'CONSUMER',
}

export enum BUSINESS_SEGMENT {
    HOME_WARRANTY = 'HOME_WARRANTY',
    COMMERCIAL = 'COMMERCIAL',
    ELECTRIFICATION = 'ELECTRIFICATION',
    MULTI_FAMILY = 'MULTI_FAMILY',
    DIGITAL_MARKETS = 'DIGITAL_MARKETS',
    MILITARY = 'MILITARY',
    ONLINE_DISTRIBUTORS = 'ONLINE_DISTRIBUTORS',
    SINGLE_FAMILY_RESIDENTIAL = 'SINGLE_FAMILY_RESIDENTIAL',
    RESIDENTIAL_NEW_CONSTRUCTION = 'RESIDENTIAL_NEW_CONSTRUCTION',
    RETAIL = 'RETAIL',
    PRIVATE_EQUITY = 'PRIVATE_EQUITY',
    ECOMMERCE = 'ECOMMERCE',
}
export type CountryAPI = {
    countryCode: string;
    createdAt: string;
    description: string;
    id: 'CA' | 'US';
    updatedAt: string;
};

export type CurrencyAPI = {
    display: string;
    id: 'USD' | 'CAD';
};

export enum DIRECTED_INVOICE_TYPE {
    CLIENT = 'CLIENT',
    CONTRACTOR = 'CONTRACTOR',
}

export type DirectedInvoiceTypesAPI = {
    display: string;
    id: keyof typeof DIRECTED_INVOICE_TYPE;
};

export enum PRICE_BOOK_TYPE {
    CLIENT = 'CLIENT',
    CONTRACTOR = 'CONTRACTOR',
}

export type PriceBookTypesAPI = DisplayType<PRICE_BOOK_TYPE>;

export enum STATES_DISPLAY {
    Alabama = 'Alabama',
    Alaska = 'Alaska',
    Arizona = 'Arizona',
    Arkansas = 'Arkansas',
    California = 'California',
    Colorado = 'Colorado',
    Connecticut = 'Connecticut',
    Delaware = 'Delaware',
    Florida = 'Florida',
    Georgia = 'Georgia',
    Hawaii = 'Hawaii',
    Idaho = 'Idaho',
    Illinois = 'Illinois',
    Indiana = 'Indiana',
    Iowa = 'Iowa',
    Kansas = 'Kansas',
    Kentucky = 'Kentucky',
    Louisiana = 'Louisiana',
    Maine = 'Maine',
    Maryland = 'Maryland',
    Massachusetts = 'Massachusetts',
    Michigan = 'Michigan',
    Minnesota = 'Minnesota',
    Mississippi = 'Mississippi',
    Missouri = 'Missouri',
    Montana = 'Montana',
    Nebraska = 'Nebraska',
    Nevada = 'Nevada',
    New_Hampshire = 'New Hampshire',
    New_Jersey = 'New Jersey',
    New_Mexico = 'New Mexico',
    New_York = 'New York',
    North_Carolina = 'North Carolina',
    North_Dakota = 'North Dakota',
    Ohio = 'Ohio',
    Oklahoma = 'Oklahoma',
    Oregon = 'Oregon',
    Pennsylvania = 'Pennsylvania',
    Rhode_Island = 'Rhode Island',
    South_Carolina = 'South Carolina',
    South_Dakota = 'South Dakota',
    Tennessee = 'Tennessee',
    Texas = 'Texas',
    Utah = 'Utah',
    Vermont = 'Vermont',
    Virginia = 'Virginia',
    Washington = 'Washington',
    West_Virginia = 'West Virginia',
    Wisconsin = 'Wisconsin',
    Wyoming = 'Wyoming',
}

export type SavedOrderVisibilityTypeAPI = DisplayType<'PRIVATE' | 'SHARED'>;

export type StateAPI = DisplayType & {
    display: keyof typeof STATES_DISPLAY;
    country: 'US' | 'Canada';
    county: string[];
};

enum UNITS_OF_MEASURE {
    hours = 'hours',
    pounds = 'pounds',
    each = 'each',
}

export type UserMention = {
    id: number;
    username: string;
};

export type UnitsofMeasureAPI = DisplayType<UNITS_OF_MEASURE> & {
    value: string;
};

export type FinancialStatusAPI = DisplayType & {
    meta: string;
};

export type ClientAPI = {
    operationInCountry: 'US' | 'Canada';
    currency: string;
    name: string;
    statusId: string;
    doingBusinessAs: string;
    website: string | null;
    id: number;
    enabledExclusiveContractorDispatching?: boolean;
    address: {
        rawAddress: string;
        stdDeliveryLine: string;
        stdLastLine: string | null;
        standardized: boolean;
        id: NumberConstructor;
    };
    settings: {
        clientId: number;
        settingTypeId: string;
        setting: {
            segment: string;
            lineOfBusiness: string;
        };
    }[];
};

export enum ORDER_LOGISTICS_TYPE {
    FACTORY_ORDER = 'FACTORY_ORDER',
    SHIPPING = 'SHIPPING',
    BACK_ORDER = 'BACK_ORDER',
    TRANSFER = 'TRANSFER',
    AVAILABLE = 'AVAILABLE',
}

export type OrderLogisticsTypeAPI = DisplayType<
    keyof typeof ORDER_LOGISTICS_TYPE
> & {
    meta: string;
};

export enum ORDER_TYPE {
    ORDER = 'ORDER',
    ORDER_WITH_ORIGIN = 'ORDER_WITH_ORIGIN',
    LABOR_WARRANTY = 'LABOR_WARRANTY',
}

export type OrderTypeAPI = DisplayType<keyof typeof ORDER_TYPE> & {
    meta: string;
};

export enum WORK_ORDER_TYPE {
    REPAIR = 'REPAIR',
    REPLACE = 'REPLACE',
    DIAGNOSTIC = 'DIAGNOSTIC',
    INSPECTION = 'INSPECTION',
    CHECKLIST = 'CHECKLIST',
    CLEAN_AND_CHECK = 'CLEAN_AND_CHECK',
    WARRANTY = 'WARRANTY',
    WALKTHROUGH = 'WALKTHROUGH',
    PREVENTATIVE_MAINTENANCE = 'PREVENTATIVE_MAINTENANCE',
}

export type WorkOrderTypeAPI = DisplayType<keyof typeof WORK_ORDER_TYPE> & {
    active: boolean;
    defaultLineItems: DefaultLineItemsAPI;
    legacyId: string;
    meta: string;
    photoRequirements: DisplayType[];
};

export type ContractorBlocklistReasonAPI = DisplayType;

export type ContractorClientAPI = {
    id: number;
    statusId: string;
    directSupply: boolean;
    directRelationActive: boolean;
    operationInCountry: 'US' | 'Canada';
    name: string;
    dsDirectedInvoiceType?: DIRECTED_INVOICE_TYPE;
    dsPriceBookType?: PRICE_BOOK_TYPE;
};

export enum CONTRACTOR_EXTERNAL_CERTIFICATION {
    NATE_CHP_5 = 'NATE_CHP_5',
    EPA_608_UNIVERSAL = 'EPA_608_UNIVERSAL',
    EPA_608_TYPE_2 = 'EPA_608_TYPE_2',
    A2L_REFRIGERANT_CERTIFIED = 'A2L_REFRIGERANT_CERTIFIED',
    NCI_COMBUSTION_PERFORMANCE_CARBON_MONOXIDE_SAFETY = 'NCI_COMBUSTION_PERFORMANCE_CARBON_MONOXIDE_SAFETY',
    ACCA_RESIDENTIAL_DESIGN_SUITE = 'ACCA_RESIDENTIAL_DESIGN_SUITE',
}

export type ContractorExternalCertificationAPI =
    DisplayType<CONTRACTOR_EXTERNAL_CERTIFICATION>;

export enum CONTRACTOR_INTERNAL_CERTIFICATION {
    MULTI_PORT_INSTALLATION = 'MULTI_PORT_INSTALLATION',
    SIDE_DISCHARGE_INVERTER_INSTALLATION = 'SIDE_DISCHARGE_INVERTER_INSTALLATION',
    CUBE_INVERTER_INSTALLATION = 'CUBE_INVERTER_INSTALLATION',
    MINI_SPLIT_SINGLE_MULTI_PORT_INSTALLATION_P1P2 = 'MINI_SPLIT_SINGLE_MULTI_PORT_INSTALLATION_P1P2',
}

export const RETIRED_CONTRACTOR_INTERNAL_CERTIFICATION_DISPLAYS: Record<
    string,
    string
> = {
    [CONTRACTOR_INTERNAL_CERTIFICATION.MINI_SPLIT_SINGLE_MULTI_PORT_INSTALLATION_P1P2]:
        'Mini Split Single/Multi Port Installation P1P2',
};

export const resolveInternalCertificationDisplay = (
    internalCertificationId: string | null | undefined,
    internalCertifications?: Array<{ id: string; display: string }>
): string | undefined => {
    if (!internalCertificationId) return undefined;
    return (
        internalCertifications?.find(c => c.id === internalCertificationId)
            ?.display ??
        RETIRED_CONTRACTOR_INTERNAL_CERTIFICATION_DISPLAYS[
            internalCertificationId
        ]
    );
};

export type ContractorInternalCertificationAPI =
    DisplayType<CONTRACTOR_INTERNAL_CERTIFICATION>;

export enum CONTRACTOR_CREDENTIAL {
    SAM_CLUB_APPROVED_CONTRACTOR = 'SAM_CLUB_APPROVED_CONTRACTOR',
    NEXT_ERA_APPROVED_CONTRACTOR = 'NEXT_ERA_APPROVED_CONTRACTOR',
    LOWES_PROVIDER_AUTHORIZED_CONTRACTOR = 'LOWES_PROVIDER_AUTHORIZED_CONTRACTOR',
    CINCH_HOME_SERVICES_UPGRADE_APPROVED = 'CINCH_HOME_SERVICES_UPGRADE_APPROVED',
    E_PREMIUM_APPROVED = 'E_PREMIUM_APPROVED',
}

export type ContractorCredentialAPI = {
    [key in CONTRACTOR_CREDENTIAL]: DisplayType<
        keyof typeof CONTRACTOR_CREDENTIAL
    > & {
        key: string;
        requiredBadge: boolean;
    };
};

export enum CONTRACTOR_DISPATCH_RULE_TYPE {
    PROJECT = 'PROJECT',
    PROBATION_HOLD = 'PROBATION_HOLD',
    VACATION = 'VACATION',
    OTHER = 'OTHER',
}
export type ContractorDispatchRuleTypesAPI = {
    [key in CONTRACTOR_DISPATCH_RULE_TYPE]: DisplayType<
        keyof typeof CONTRACTOR_DISPATCH_RULE_TYPE
    >;
};

export enum CONTRACTOR_STATUS {
    ACCEPTED = 'ACCEPTED',
    APPROVED = 'APPROVED',
    ENABLED = 'ENABLED',
    INACTIVE = 'INACTIVE',
    INSURANCE_EXPIRED = 'INSURANCE_EXPIRED',
    PENDING_APPROVAL = 'PENDING_APPROVAL',
}

export type ContractorStatusAPI = {
    [key in CONTRACTOR_STATUS]: DisplayType<keyof typeof CONTRACTOR_STATUS> & {
        active: true;
    };
};

// transformed type
export type ContractorStatus = DisplayType<keyof typeof CONTRACTOR_STATUS> & {
    active: true;
};

export enum DRAFT_STATUS {
    CREATED = 'CREATED',
    CANCELLED = 'CANCELLED',
    COMPLETED = 'COMPLETED',
}

export type DraftStatusAPI = DisplayType<DRAFT_STATUS> & {
    contractor: {
        contractor_owner: boolean;
        contractor_admin: boolean;
        contractor_manager: boolean;
        contractor_user: boolean;
    };
    client: {
        client_owner: boolean;
        client_admin: boolean;
        client_manager: boolean;
        client_user: boolean;
    };
    motili: {
        motili_owner: boolean;
        motili_admin: boolean;
        motili_manager: boolean;
        motili_user: boolean;
    };
};

export enum CLIENT_STATUS {
    ACTIVE = 'ACTIVE',
    APPROVED = 'APPROVED',
    ENABLED = 'ENABLED',
    INACTIVE = 'INACTIVE',
    ON_HOLD = 'ON_HOLD',
    PENDING_APPROVAL = 'PENDING_APPROVAL',
}

export type ClientStatusAPI = {
    [key in CLIENT_STATUS]: DisplayType<keyof typeof CLIENT_STATUS> & {
        active: true;
    };
};

// transformed type
export type ClientStatus = DisplayType<keyof typeof CLIENT_STATUS> & {
    active: true;
};

export enum WORK_ORDER_STATUS {
    INITIALIZED = 'INITIALIZED',
    CREATED = 'CREATED',
    CREATING = 'CREATING',
    ORDERED = 'ORDERED',
    SCHEDULING = 'SCHEDULING',
    SCHEDULED = 'SCHEDULED',
    CONTACT_REQUIRED = 'CONTACT_REQUIRED',
    DISPATCHING = 'DISPATCHING',
    DISPATCHED = 'DISPATCHED',
    NO_CONTRACTOR_FOUND = 'NO_CONTRACTOR_FOUND',
    DELAYED = 'DELAYED',
    CANCELLED = 'CANCELLED',
    IN_PROCESS = 'IN_PROCESS',
    IN_REVIEW = 'IN_REVIEW',
    SUBMITTED = 'SUBMITTED',
    COMPLETED = 'COMPLETED',
    CLOSED = 'CLOSED',
    DRAFTED = 'DRAFTED',
    DRAFT_CANCELLED = 'DRAFT_CANCELLED',
}

export type WorkOrderStatusAPI = {
    id: keyof typeof WORK_ORDER_STATUS;
    display: string;
    meta: string;
    explanationText: string | Record<string, string>;
    cancelable: boolean;
    lineItemsEditable: {
        contractor: {
            contractor_owner: boolean;
            contractor_admin: boolean;
            contractor_manager: boolean;
            contractor_user: boolean;
        };
        client: {
            client_owner: boolean;
            client_admin: boolean;
            client_manager: boolean;
            client_user: boolean;
        };
        motili: {
            motili_owner: boolean;
            motili_admin: boolean;
            motili_manager: boolean;
            motili_user: boolean;
        };
    };
    system: false;
    contractor: false;
};

export enum QUOTE_STATUS {
    SUBMITTED = 'SUBMITTED',
    CREATING = 'CREATING',
    CREATED = 'CREATED',
    REVIEWED = 'REVIEWED',
    REVISING = 'REVISING',
    APPROVED = 'APPROVED',
    CANCELLED = 'CANCELLED',
    ORDERED = 'ORDERED',
    COMPLETED = 'COMPLETED',
    CLOSED = 'CLOSED',
    DRAFTED = 'DRAFTED',
    DRAFT_CANCELLED = 'DRAFT_CANCELLED',
}

export type QuoteStatusAPI = {
    id: keyof typeof QUOTE_STATUS;
    display: string;
    meta: string;
    explanationText: string | Record<string, string>;
    cancelable: boolean;
    lineItemsEditable: {
        contractor: {
            contractor_owner: boolean;
            contractor_admin: boolean;
            contractor_manager: boolean;
            contractor_user: boolean;
        };
        client: {
            client_owner: boolean;
            client_admin: boolean;
            client_manager: boolean;
            client_user: boolean;
        };
        motili: {
            motili_owner: boolean;
            motili_admin: boolean;
            motili_manager: boolean;
            motili_user: boolean;
        };
    };
    system: false;
    contractor: false;
};

export enum ORDER_MANUAL_VENDOR_DETAIL_TYPES {
    MANUAL_VENDOR_ORDER_NUMBER = 'MANUAL_VENDOR_ORDER_NUMBER',
    MANUAL_TRACKING_NUMBER = 'MANUAL_TRACKING_NUMBER',
    VERIFY_VENDOR_ORDER_NUMBER = 'VERIFY_VENDOR_ORDER_NUMBER',
    MANUAL_SSCC_ID = 'MANUAL_SSCC_ID',
}

export type OrderManualVendorDetailTypesApi = DisplayType<
    keyof typeof ORDER_MANUAL_VENDOR_DETAIL_TYPES
>;

export enum ORDER_RETURN_CANCEL_REASON {
    REQUESTED_IN_ERROR = 'REQUESTED_IN_ERROR',
    TECHNICAL_ISSUE = 'TECHNICAL_ISSUE',
    CONTRACTOR_HOMEOWNER_KEPT_ITEMS = 'CONTRACTOR_HOMEOWNER_KEPT_ITEMS',
    COULD_NOT_RETURN_ITEMS = 'COULD_NOT_RETURN_ITEMS',
    DISTRIBUTION_DECLINED_RETURN = 'DISTRIBUTION_DECLINED_RETURN',
    OTHER = 'OTHER',
}

export type OrderReturnCancelReasonApi = DisplayType<
    keyof typeof ORDER_RETURN_CANCEL_REASON
> & { meta: string; obsolete?: boolean };

export enum ORDER_RETURN_ATTACHMENT_TYPE {
    BILL_OF_LADING = 'BILL_OF_LADING',
    FA_PROOF_OF_RETURN = 'FA_PROOF_OF_RETURN',
    FA_VENDOR_CREDIT = 'FA_VENDOR_CREDIT',
    PROOF_OF_DAMAGE = 'PROOF_OF_DAMAGE',
    PROOF_OF_DELIVERY = 'PROOF_OF_DELIVERY',
}

export type OrderReturnAttachmentTypeApi = DisplayType<
    keyof typeof ORDER_RETURN_ATTACHMENT_TYPE
> & { meta: string; obsolete?: boolean };

export enum ORDER_RETURN_REASON {
    DISTRIBUTOR_CONCESSION = 'DISTRIBUTOR_CONCESSION',
    EQUIPMENT_RETURN = 'EQUIPMENT_RETURN',
    WARRANTY = 'WARRANTY',
    DAMAGED_RETURN = 'DAMAGED_RETURN',
    LOST_MISSING = 'LOST_MISSING',
    DOA = 'DOA',
    FINANCE_EDIT = 'FINANCE_EDIT',
    OTHER = 'OTHER',
}

export type OrderReturnReasonApi = DisplayType<
    keyof typeof ORDER_RETURN_REASON
> & {
    meta: string;
    obsolete?: boolean;
    warrantyVendor?: { id: number; name: string };
    costEditable?: boolean;
    priceEditable?: boolean;
    reasons?: [];
};

export enum ORDER_RETURN_CONTACT_TYPE {
    CT_RETURN_CONTACT = 'CT_RETURN_CONTACT',
    CT_RETURN_CLIENT_CONTACT = 'CT_RETURN_CLIENT_CONTACT',
}

export type OrderReturnContactTypeApi = DisplayType<
    keyof typeof ORDER_RETURN_CONTACT_TYPE
> & {
    description: string;
    img: string;
};

export enum ORDER_RETURN_STATUS {
    REQUESTED = 'REQUESTED',
    ACTIVE = 'ACTIVE',
    CANCELLED = 'CANCELLED',
    COMPLETED = 'COMPLETED',
    CLOSED = 'CLOSED',
    DENIED = 'DENIED',
    PARTIAL_APPROVED = 'PARTIAL_APPROVED',
    APPROVED = 'APPROVED',
}

export type OrderReturnStatusApi = {
    id: keyof typeof ORDER_RETURN_STATUS;
    display: string;
    approvalReasons?: any;
};

export enum ORDER_STATUS {
    SUBMITTED = 'SUBMITTED',
    CREATED = 'CREATED',
    ORDER_REQUESTED = 'ORDER_REQUESTED',
    PROCESSING = 'PROCESSING',
    ORDERED = 'ORDERED',
    ORDERED_CONFIRM = 'ORDERED_CONFIRM',
    COMPLETED = 'COMPLETED',
    RECONCILING = 'RECONCILING',
    CANCEL_REQUESTED = 'CANCEL_REQUESTED',
    CANCELLING = 'CANCELLING',
    CANCELLED = 'CANCELLED',
    CLOSED = 'CLOSED',
    HOLD = 'HOLD',
    IN_PROCESS = 'IN_PROCESS',
}

export enum ORDER_CLIENT_ORDER_STATUS {
    APPROVAL_REQUIRED = 'APPROVAL_REQUIRED',
}

export type OrderStatusAPI = {
    id: keyof typeof ORDER_STATUS;
    display: string;
    meta: string;
    explanationText: string | Record<string, string>;
    cancelable: boolean;
    lineItemsEditable: {
        contractor: {
            contractor_owner: boolean;
            contractor_admin: boolean;
            contractor_manager: boolean;
            contractor_user: boolean;
        };
        client: {
            client_owner: boolean;
            client_admin: boolean;
            client_manager: boolean;
            client_user: boolean;
        };
        motili: {
            motili_owner: boolean;
            motili_admin: boolean;
            motili_manager: boolean;
            motili_user: boolean;
        };
    };
    system: false;
    locked: {
        contractor: {
            contractor_owner: boolean;
            contractor_admin: boolean;
            contractor_manager: boolean;
            contractor_user: boolean;
        };
        client: {
            client_owner: boolean;
            client_admin: boolean;
            client_manager: boolean;
            client_user: boolean;
        };
        motili: {
            motili_owner: boolean;
            motili_admin: boolean;
            motili_manager: boolean;
            motili_user: boolean;
        };
    };
};

export enum ACCOUNT_TYPE {
    motili = 'motili',
    contractor = 'contractor',
    client = 'client',
    application = 'application',
}

export type AccountTypeAPI = DisplayType<keyof typeof ACCOUNT_TYPE>;

export type AccountRoleAPI = {
    description: string;
    name: string;
};

export type AccountRoleTypeAPI = DisplayType & {
    commentTypeId: string;
    motili: boolean;
    shortDisplay: string;
};

enum ACCOUNT_PREFERENCE_TYPE {
    sms = 'sms',
    email = 'email',
}

export type AccountPreferenceTypeAPI = DisplayType<ACCOUNT_PREFERENCE_TYPE>;

export enum WARRANTY_CONTACT_TYPE {
    HOMEOWNER = 'HOMEOWNER',
    MAILING = 'MAILING',
    INSTALLED_AT = 'INSTALLED_AT',
    CUSTOMER = 'CUSTOMER',
    INSTALLER = 'INSTALLER',
}

export type WarrantyContactTypeAPI = {
    id: WARRANTY_CONTACT_TYPE;
    display: string;
    payloadKey: string;
    description: string;
};

export enum WARRANTY_CONTACT_ADDRESS_TYPE {
    PROPERTY = 'PROPERTY',
    CUSTOM = 'CUSTOM',
}

export enum WARRANTY_TYPE {
    EQUIPMENT = 'EQUIPMENT',
    LABOR = 'LABOR',
}

export enum WARRANTY_OWNER_TYPE {
    DYNAMIC = 'DYNAMIC',
    FIXED = 'FIXED',
}

export type WarrantyOwnerTypeAPI = DisplayType<WARRANTY_OWNER_TYPE>;

export enum WARRANTY_OWNER_INSTALL_TYPE {
    RESIDENTIAL = 'RESIDENTIAL',
    COMMERCIAL = 'COMMERCIAL',
    MULTI_FAMILY = 'MULTI_FAMILY',
    APARTMENT = 'APARTMENT',
    NEW_CONSTRUCTION = 'NEW_CONSTRUCTION',
}

export type WarrantyOwnerInstallTypeAPI = {
    id: WARRANTY_OWNER_INSTALL_TYPE;
    value: WARRANTY_OWNER_INSTALL_TYPE;
    label: string;
    sortOrder: number;
    active: boolean;
};

export enum WARRANTY_REGISTRATION_STATES {
    INITIAL = 'INITIAL',
    TRANSIENT = 'TRANSIENT',
    TERMINAL = 'TERMINAL',
}

export enum WARRANTY_REGISTRATION_STATUSES {
    RECEIVED = 'RECEIVED',
    SCHEDULED = 'SCHEDULED',
    QUEUED = 'QUEUED',
    PROCESSING = 'PROCESSING',
    SUBMITTED = 'SUBMITTED',
    REJECTED = 'REJECTED',
    REGISTERED = 'REGISTERED',
    NOT_REGISTERED = 'NOT_REGISTERED',
    PENDING_SUBMISSION = 'PENDING_SUBMISSION',
    TRY_AGAIN_LATER = 'TRY_AGAIN_LATER',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
    EXPIRED = 'EXPIRED',
}

export type WarrantyRegistrationStatusesAPI =
    DisplayType<WARRANTY_REGISTRATION_STATUSES> & {
        state: WARRANTY_REGISTRATION_STATES;
    };

export enum LABOR_WARRANTY_EXTENSION_STATUSES {
    CREATED = 'CREATED',
    PROCESSING = 'PROCESSING',
    REJECTED = 'REJECTED',
    PENDING_SUBMISSION = 'PENDING_SUBMISSION',
    TRY_AGAIN_LATER = 'TRY_AGAIN_LATER',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
}

export type LaborWarrantyExtensionStatusesAPI =
    DisplayType<LABOR_WARRANTY_EXTENSION_STATUSES>;

export enum WORK_ORDER_CONTACT_PREFERENCE_TYPE {
    EMAIL = 'EMAIL',
    MOBILE = 'MOBILE',
    PHONE_LANDLINE = 'PHONE_LANDLINE',
    OTHER = 'OTHER',
}

export type WorkOrderContactPreferenceTypeAPI =
    DisplayType<WORK_ORDER_CONTACT_PREFERENCE_TYPE>;

export type WorkOrderCheckInCheckOutReasonTypeAPI = DisplayType;

export type FinancialTermAPI = DisplayType & {
    active: boolean;
    days: number;
    description: string;
    name: string;
    purchase: boolean;
    sales: boolean;
};

export enum FOLDER_ATTACHMENT_TYPE {
    FA_MAS90 = 'FA_MAS90',
    FA_COMMERCIAL_PROPOSAL = 'FA_COMMERCIAL_PROPOSAL',
    FA_EQUIPMENT_WARRANTY = 'FA_EQUIPMENT_WARRANTY',
    FA_INVOICE = 'FA_INVOICE',
    FA_OWNER_MANUAL = 'FA_OWNER_MANUAL',
    FA_PERMIT = 'FA_PERMIT',
    FA_QUOTE_CARRIER = 'FA_QUOTE_CARRIER',
    FA_QUOTE_MANUFACTURER = 'FA_QUOTE_MANUFACTURER',
    FA_CLIENT_PO = 'FA_CLIENT_PO',
    FA_QUOTE_APPROVAL = 'FA_QUOTE_APPROVAL',
    FA_EMAIL_COMMUNICATION = 'FA_EMAIL_COMMUNICATION',
    FA_QUOTE_PDF = 'FA_QUOTE_PDF',
    FA_CONTRACTOR_QUOTE_PDF = 'FA_CONTRACTOR_QUOTE_PDF',
    FA_PHOTO_ONLY_PDF = 'FA_PHOTO_ONLY_PDF',
    FA_ORDER_PDF = 'FA_ORDER_PDF',
    FA_SUMMARY_OF_WORK_PDF = 'FA_SUMMARY_OF_WORK_PDF',
    FA_PROOF_OF_RETURN = 'FA_PROOF_OF_RETURN',
    FA_VENDOR_CREDIT = 'FA_VENDOR_CREDIT',
    FA_CHANGE_ORDER = 'FA_CHANGE_ORDER',
    FA_CANCELLATION = 'FA_CANCELLATION',
    FA_POTENTIAL_DAMAGE = 'FA_POTENTIAL_DAMAGE',
    HVAC_INSPECTION_CHECKLIST = 'HVAC_INSPECTION_CHECKLIST',
    RECEIPT = 'RECEIPT',
    DAMAGE_VERIFICATION = 'DAMAGE_VERIFICATION',
    PROOF_OF_DELIVERY_BOL = 'PROOF_OF_DELIVERY_BOL',
    LOWNAX_WAIVER = 'LOWNAX_WAIVER',
    PROOF_OF_DELIVERY = 'PROOF_OF_DELIVERY',
    BILL_OF_LADING = 'BILL_OF_LADING',
}

export type FolderAttachmentTypeAPI = DisplayType<
    keyof typeof FOLDER_ATTACHMENT_TYPE
> & {
    active: boolean;
    adminReadOnly: boolean;
    adminWriteOnly: boolean;
    clientRead: boolean;
    clientWrite: boolean;
    contractorRead: boolean;
    contractorWrite: boolean;
    contractorWriteDocumentTypes: string[];
    opsRead: boolean;
    opsWrite: boolean;
};

enum ADJUSTMENT_TYPE {
    MARKUP = 'MARKUP',
    MARGIN = 'MARGIN',
    FIXED = 'FIXED',
}

export type AdjustmentTypeAPI = DisplayType<keyof typeof ADJUSTMENT_TYPE> & {
    id: string;
    display: string;
    type: string;
    min: number;
    max: number;
    financeEdit: boolean;
};

enum LINE_ITEM_ADJUSTMENT_REASON_TYPE {
    DISCOUNT_TO_WIN_THE_JOB = 'DISCOUNT_TO_WIN_THE_JOB',
    PER_CLIENT_PRICING = 'PER_CLIENT_PRICING',
    PRICE_MATCH = 'PRICE_MATCH',
    PROJECT_DISCOUNT = 'PROJECT_DISCOUNT',
    CUSTOMER_RETENTION = 'CUSTOMER_RETENTION',
    SURPLUS_INVENTORY = 'SURPLUS_INVENTORY',
    NEEDED_FOR_BUNDLE_INSTALLATION = 'NEEDED_FOR_BUNDLE_INSTALLATION',
    LINE_ITEM_DETAIL_UPDATE = 'LINE_ITEM_DETAIL_UPDATE',
}

export type LineItemAdjustmentReasonTypeAPI = DisplayType<
    keyof typeof LINE_ITEM_ADJUSTMENT_REASON_TYPE
> & {
    id: string;
    display: string;
    type: string;
    min: number;
    max: number;
    financeEdit: boolean;
};

export enum LINE_ITEM_STATUSES {
    CREATED = 'CREATED',
    APPROVED = 'APPROVED',
    COMPLETED = 'COMPLETED',
    RETURNING = 'RETURNING',
    RETURNED = 'RETURNED',
    PARTIALLY_RETURNED = 'PARTIALLY_RETURNED',
}
export type LineItemStatusesAPI = DisplayType<
    keyof typeof LINE_ITEM_STATUSES
> & {
    id: string;
    display: string;
    meta: string;
};

enum PROJECT_ATTACHMENT_TYPE {
    PJFA_BID = 'PJFA_BID',
    PJFA_CLIENT_CONTRACT = 'PJFA_CLIENT_CONTRACT',
    PJFA_CONTRACTOR_CONTRACT = 'PJFA_CONTRACTOR_CONTRACT',
    PJFA_PAYMENT_TERMS = 'PJFA_PAYMENT_TERMS',
    PJFA_PROPOSAL = 'PJFA_PROPOSAL',
    PJFA_SCHEDULE = 'PJFA_SCHEDULE',
    PJFA_SITE_MAP = 'PJFA_SITE_MAP',
    PJFA_CHANGE_ORDER = 'PJFA_CHANGE_ORDER',
    PJFA_DEDUCTIONS = 'PJFA_DEDUCTIONS',
}

export type ProjectAttachmentTypeAPI = DisplayType<PROJECT_ATTACHMENT_TYPE> & {
    adminReadOnly: boolean;
    adminWriteOnly: boolean;
    opsRead: boolean;
    opsWrite: boolean;
    contractorRead: boolean;
    contractorWrite: boolean;
    clientRead: boolean;
    clientWrite: boolean;
    active: boolean;
};

enum PROPERTY_ATTACHMENT_TYPE {
    PFA_GENERAL = 'PFA_GENERAL',
}

export type PropertyAttachmentTypeAPI =
    DisplayType<PROPERTY_ATTACHMENT_TYPE> & {
        adminReadOnly: boolean;
        adminWriteOnly: boolean;
        opsRead: boolean;
        opsWrite: boolean;
        contractorRead: boolean;
        contractorWrite: boolean;
        clientRead: boolean;
        clientWrite: boolean;
        active: boolean;
    };

export enum TEMPLATE_TYPE {
    CLIENT_TEMPLATE = 'CLIENT_TEMPLATE',
    QUOTE_TEMPLATE = 'QUOTE_TEMPLATE',
    CHECKLIST_TEMPLATE = 'CHECKLIST_TEMPLATE',
}
enum TICKET_PROVIDER {
    FRESHDESK = 'FRESHDESK',
}

export type TicketProviderAPI = DisplayType<TICKET_PROVIDER> & {
    active: boolean;
};

export type TemplateTypeAPI = DisplayType<TEMPLATE_TYPE>;

export enum PROPERTY_TYPE {
    HOUSE = 'HOUSE',
    APARTMENT = 'APARTMENT',
}

export type PropertyTypeAPI = DisplayType<PROPERTY_TYPE> & {
    baseMultiFamily: boolean;
    baseRdi: string;
    categorizations: string[];
    description: string;
    meta: string;
};

export enum ORDER_CONTACT_TYPE {
    CT_CONTRACTOR = 'CT_CONTRACTOR',
    CT_OTHER = 'CT_OTHER',
    CT_HOMEOWNER = 'CT_HOMEOWNER',
    CT_COMPANY = 'CT_COMPANY',
}

export enum NOTIFICATION_TYPE {
    ALL = 'ALL',
    COMPLETED = 'COMPLETED',
    OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
    READY_FOR_PICKUP = 'READY_FOR_PICKUP',
}

export type OrderNotificationTypeAPI = DisplayType<NOTIFICATION_TYPE> & {
    mapping: string[];
};

export type OrderContactTypeAPI = DisplayType<
    keyof typeof ORDER_CONTACT_TYPE
> & {
    notifications: {
        [key in NOTIFICATION_TYPE]: {
            id: keyof typeof NOTIFICATION_TYPE;
            display: string;
            mapping: string[];
        };
    };
    description: string;
    img: string;
};

export enum WORK_ORDER_CONTACT_TYPE {
    CT_OCCUPANT = 'CT_OCCUPANT',
    CT_OTHER = 'CT_OTHER',
    CT_PROPERTY_MANAGER = 'CT_PROPERTY_MANAGER',
    CT_GENERAL_CONTRACTOR = 'CT_GENERAL_CONTRACTOR',
}

export type WorkOrderContactTypeAPI = DisplayType<
    keyof typeof WORK_ORDER_CONTACT_TYPE
> & {
    notifications: {
        [key in NOTIFICATION_TYPE]: {
            id: keyof typeof NOTIFICATION_TYPE;
            display: string;
            mapping: string[];
        };
    };
    description: string;
    img: string;
};

export type VendorAPI = {
    currency?: string;
    doingBusinessAs: string;
    id: number;
    name: string;
    operationInCountry?: string;
    website: string;
    typeId: string;
};

enum PROJECT_CONTACT_TYPE {
    CT_CLIENT = 'CT_CLIENT',
    CT_OTHER = 'CT_OTHER',
    CT_CONTRACTOR = 'CT_CONTRACTOR',
    CT_INTERNAL = 'CT_INTERNAL',
    CT_PROPERTY_MANAGER = 'CT_PROPERTY_MANAGER',
    CT_VENDOR = 'CT_VENDOR',
}

export type ProjectContactTypeAPI = DisplayType<
    keyof typeof PROJECT_CONTACT_TYPE
> & {
    notifications: {
        [key in NOTIFICATION_TYPE]: {
            id: keyof typeof NOTIFICATION_TYPE;
            display: string;
            mapping: string[];
        };
    };
    description: string;
    img: string;
};

export enum WORK_ORDER_PRIORITY_TYPE {
    EMERGENCY = 'EMERGENCY',
    ROUTINE = 'ROUTINE',
    URGENT = 'URGENT',
}

export enum ORDER_PRIORITY_TYPE {
    EMERGENCY = 'EMERGENCY',
    ROUTINE = 'ROUTINE',
    URGENT = 'URGENT',
}

export type WorkOrderPriorityTypeAPI = DisplayType<
    keyof typeof WORK_ORDER_PRIORITY_TYPE
> & {
    responseTime: number;
    responseTimeDisplay: string;
    completionTime: number;
    completionTimeDisplay: string;
    workOrderTypes: (keyof typeof WORK_ORDER_TYPE)[];
};

export type OrderPriorityTypeAPI = DisplayType<
    keyof typeof ORDER_PRIORITY_TYPE
>;

export enum ORDER_FULFILLMENT_METHOD {
    DELIVERY_LTL = 'DELIVERY_LTL',
    DELIVERY_PARCEL = 'DELIVERY_PARCEL',
    BRANCH_STOCK = 'BRANCH_STOCK',
    FACTORY_DIRECT = 'FACTORY_DIRECT',
    CLIENT_PROVIDED_BOL = 'CLIENT_PROVIDED_BOL',
    AMAZON_DELIVERY_LTL = 'AMAZON_DELIVERY_LTL',
    AMAZON_PROVIDED_BOL = 'AMAZON_PROVIDED_BOL',
    BULK = 'BULK',
    PICK_UP = 'PICK_UP',
    SPECIAL = 'SPECIAL',
}
export type orderFulfillmentMethodAPI =
    DisplayType<ORDER_FULFILLMENT_METHOD> & {
        billingThreshold: number;
        fulfillmentTypeId: ORDER_FULFILLMENT_TYPE;
        hasFreight: boolean;
    };

export enum PRODUCT_ATTRIBUTE_TYPE {
    CAPACITY = 'CAPACITY',
    DIMENSIONS = 'DIMENSIONS',
    PERFORMANCE = 'PERFORMANCE',
    PROPERTY = 'PROPERTY',
    RATING = 'RATING',
    TYPE = 'TYPE',
}

export type ProductAttributeTypeAPI = DisplayType<
    keyof typeof PRODUCT_ATTRIBUTE_TYPE
> & {
    meta: string;
    subTypes: {
        [key: string]: DisplayType & {
            featureDisplayPrefix: string;
            description: string;
            isFeature: boolean;
        };
    };
};

export enum CLIENT_FREIGHT_TYPE {
    FLAT_RATE = 'FLAT_RATE',
    FLAT_RATE_ORDER_TYPE = 'FLAT_RATE_ORDER_TYPE',
    RANGED_RATE = 'RANGED_RATE',
    NO_FREIGHT = 'NO_FREIGHT',
    ORDER_RULE = 'ORDER_RULE',
}

export type ClientFreightTypeAPI = DisplayType<
    keyof typeof CLIENT_FREIGHT_TYPE
> & {
    onlineDistributorOnly: boolean;
};

export enum CLIENT_INTEGRATION_TYPE {
    EMAIL = 'EMAIL',
    DIRECT = 'DIRECT',
    COUPA_CXML = 'COUPA_CXML',
    OUTBOUND_INVOICE = 'OUTBOUND_INVOICE',
    NOTIFICATION = 'NOTIFICATION',
}

export type ClientIntegrationTypeAPI = DisplayType<
    keyof typeof CLIENT_INTEGRATION_TYPE
> & {
    providers?: {
        [key: string]: DisplayType;
    };
    types?: {
        [key in CLIENT_INTEGRATION_TYPE]?: DisplayType;
    };
    events?: {
        [key: string]: DisplayType & {
            documentTypeIds: string[];
            userTypes: string[];
        };
    };
    outputTypes?: {
        [key: string]: DisplayType;
    };
    authentificationTypes?: {
        NONE: DisplayType;
        BASIC: DisplayType;
        API_KEY: DisplayType & {
            addToTypes: {
                HEADER: DisplayType;
                QUERY_PARAMS: DisplayType;
            };
        };
        BEARER_TOKEN: DisplayType;
    };
    integration: {
        scope: string;
        schema: {
            title: string;
            type: SchemaType;
            properties: {
                active: {
                    type: SchemaType;
                };
                clientId: {
                    type: SchemaType;
                };
                typeId: {
                    type: SchemaType;
                };
                config: {
                    type: SchemaType;
                };
                name: {
                    type: SchemaType;
                };
            };
            required: string[];
        };
    };
};

export type TradingServiceDefaultKey =
    'INVOICED' | 'ORDERED' | 'CANCELLED' | 'SHIPPED';

export type TradingServiceDefaultKind =
    'INTEGRATION' | 'STATUS_CHANGED_NOTIFICATION';

export type TradingServiceDefaultOutcome = 'created' | 'skipped' | 'failed';

export type TradingServiceDefinitionTypeAPI =
    DisplayType<TradingServiceDefaultKey> & {
        kind: TradingServiceDefaultKind;
        pathSuffix: string;
        statusId?: string;
    };

export type ClientTradingServiceConstantsAPI = {
    KINDS: Record<TradingServiceDefaultKind, TradingServiceDefaultKind>;
    OUTCOMES: Record<
        'CREATED' | 'SKIPPED' | 'FAILED',
        TradingServiceDefaultOutcome
    >;
    DEFINITION_TYPES: Record<
        TradingServiceDefaultKey,
        TradingServiceDefinitionTypeAPI
    >;
};

export type ClientStatusChangedNotificationConfigAPI = {
    schema: {
        title: string;
        type: string;
        properties: {
            active: {
                type: string;
            };
            name: {
                type: string;
            };
            clientId: {
                type: string;
            };
            objectTypeId: {
                type: string;
            };
            config: {
                type: string;
            };
        };
        required: string[];
    };
    outputTypes: {
        JSON: DisplayType;
    };
    authenticationTypes: {
        NONE: DisplayType;
        BASIC: DisplayType;
        API_KEY: DisplayType;
        BEARER_TOKEN: DisplayType;
    };
    addToTypes: {
        HEADER: DisplayType;
        QUERY_PARAMS: DisplayType;
    };
    objectConfigs: {
        [key: string]: {
            id: string;
            name: string;
            property: string;
            attributes: string[];
            filterAttributes: (DisplayType & {
                values: (DisplayType & {
                    legacyId: string;
                    active: boolean;
                    meta: string;
                    defaultLineItems: DefaultLineItemsAPI;
                    photoRequirements: {
                        id: string;
                        display: string;
                    }[];
                })[];
            })[];
        };
    };
};

export enum PROJECT_HOLD_REASON_TYPE {
    CLIENT_REQUEST = 'CLIENT_REQUEST',
    FINANCIAL_REQUEST = 'FINANCIAL_REQUEST',
    PHASED = 'PHASED',
    WEATHER = 'WEATHER',
    OTHER = 'OTHER',
}

export type ProjectHoldReasonTypeAPI = DisplayType<
    keyof typeof PROJECT_HOLD_REASON_TYPE
>;

export enum SCHEDULE_TYPES {
    CALENDAR_SCHEDULE = 'CALENDAR_SCHEDULE',
    MOTILI_SCHEDULE = 'MOTILI_SCHEDULE',
}

export enum CLIENT_SETTINGS {
    ASSET_MICROSITE = 'ASSET_MICROSITE',
    BRANDING = 'BRANDING',
    CLIP_LEVEL = 'CLIP_LEVEL',
    CONTACT_SCHEDULING = 'CONTACT_SCHEDULING',
    DRAFTS = 'DRAFTS',
    DYNAMIC_DISPATCHING = 'DYNAMIC_DISPATCHING',
    EXPORTS = 'EXPORTS',
    FREIGHT = 'FREIGHT',
    LINE_OF_BUSINESS = 'LINE_OF_BUSINESS',
    MESSAGING = 'MESSAGING',
    ORDER_MANAGEMENT = 'ORDER_MANAGEMENT',
    REMINDER_SETTING = 'REMINDER_SETTING',
    SLA = 'SLA',
    INSTRUCTIONS = 'INSTRUCTIONS',
    WARRANTY = 'WARRANTY',
    EQUIPMENT_ONLY_QUOTE = 'EQUIPMENT_ONLY_QUOTE',
    TERM_NET = 'TERM_NET',
}

export enum TSM_REGIONS {
    BAL = 'BAL',
    LAW = 'LAW',
    PHI = 'PHI',
    UMW = 'UMW',
    GEO = 'GEO',
    CFL = 'CFL',
    NFL = 'NFL',
    SFL = 'SFL',
    DMR = 'DMR',
    NCA = 'NCA',
    SCA = 'SCA',
    LAR = 'LAR',
    CHI = 'CHI',
    GMW = 'GMW',
    MID = 'MID',
    DAL = 'DAL',
    HOU = 'HOU',
    SAN = 'SAN',
    AUS = 'AUS',
}

export type TSM_Regions_API = DisplayType<keyof typeof TSM_REGIONS> & {
    division: string;
};

export type LHSRiskType = DisplayType & {
    reasons: {
        [reason: string]: DisplayType;
    };
};

export enum WORK_ORDER_REASSIGNMENT_REASONS {
    IN_THE_AREA = 'IN_THE_AREA',
    AVAILABLE_FOR_SCHEDULE = 'AVAILABLE_FOR_SCHEDULE',
    REASSIGN_TO_CORRECT_TECHNICIAN = 'REASSIGN_TO_CORRECT_TECHNICIAN',
    OTHER = 'OTHER',
}

export type WorkOrderReassignmentReasonAPI = DisplayType<
    keyof typeof WORK_ORDER_REASSIGNMENT_REASONS
>;

export enum WORK_ORDER_UNASSIGN_REASONS {
    ACCEPTED_IN_ERROR = 'ACCEPTED_IN_ERROR',
    ALREADY_AGREED_TO_WORK = 'ALREADY_AGREED_TO_WORK',
    CANT_MAKE_TIMEFRAME = 'CANT_MAKE_TIMEFRAME',
    CLIENT_REQUEST = 'CLIENT_REQUEST',
    CONTRACTOR_DECLINED = 'CONTRACTOR_DECLINED',
    OUT_OF_AREA = 'OUT_OF_AREA',
    PREFERRED_CONTRACTOR = 'PREFERRED_CONTRACTOR',
    RESCHEDULE = 'RESCHEDULE',
    UNRESPONSIVE = 'UNRESPONSIVE',
    OTHER = 'OTHER',
}

export type WorkOrderUnassignReasonAPI = DisplayType<
    keyof typeof WORK_ORDER_UNASSIGN_REASONS
>;

export enum WORK_ORDER_RESCHEDULE_REASONS {
    GATHER_MISSING_INFORMATION = 'GATHER_MISSING_INFORMATION',
    COMPLETE_EQUIPMENT_WORK = 'COMPLETE_EQUIPMENT_WORK',
    REPLACE_A_COMPONENT = 'REPLACE_A_COMPONENT',
    OTHER = 'OTHER',
}

export type WorkOrderRescheduleReasonAPI = DisplayType<
    keyof typeof WORK_ORDER_RESCHEDULE_REASONS
>;

export type TimezoneAPI = DisplayType;

export type OperationInCountry = 'US' | 'CA';

export enum ROLES {
    MOTILI_OWNER = 'motili_owner',
    MOTILI_ADMIN = 'motili_admin',
    MOTILI_MANAGER = 'motili_manager',
    MOTILI_USER = 'motili_user',
    CONTRACTOR_OWNER = 'contractor_owner',
    CONTRACTOR_ADMIN = 'contractor_admin',
    CONTRACTOR_MANAGER = 'contractor_manager',
    CONTRACTOR_USER = 'contractor_user',
    CLIENT_OWNER = 'client_owner',
    CLIENT_ADMIN = 'client_admin',
    CLIENT_MANAGER = 'client_manager',
    CLIENT_USER = 'client_user',
    CLIENT_UTILITY = 'client_utility',
    ON_SITE_MOBILE_PRE_RELEASE_USER = 'on_site_mobile_pre_release_user',
    ON_SITE_MOBILE_USER = 'on_site_mobile_user',
    ON_SITE_MOBILE_TESTER = 'on_site_mobile_tester',
    CHILD_CLIENT_ACCESS = 'child_client_access',
}
